import * as React from "react";
import TextField from "@mui/material/TextField";
import { Box, Typography, IconButton } from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { HelpTooltip } from "../help/HelpTooltip";

import { Controller, useFormContext } from "react-hook-form";
import type { FieldValues, FieldPath } from "react-hook-form";
import { useTranslation } from "react-i18next";

type BaseTextFieldProps = Omit<
  React.ComponentProps<typeof TextField>,
  "name" | "value" | "onChange" | "onBlur" | "ref" | "label"
>;

export type TextInputProps<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>
> = BaseTextFieldProps & {
  name: TName;
  label: React.ReactNode;
  required?: boolean;
  tooltip?: string;
  helperText?: React.ReactNode;
};

export function TextInput<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>
>({
  name,
  label,
  required,
  tooltip,
  helperText,
  ...rest
}: TextInputProps<TFieldValues, TName>) {
  const { control } = useFormContext<TFieldValues>();
  const { t } = useTranslation();

  const translateError = (m?: string) => (m ? t(m, { defaultValue: m }) : "");

  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState }) => {
        const errorMsg = fieldState.error?.message;

        return (
          <Box>
            {/* LABEL + TOOLTIP + ASTERISCO */}
            <Box
              sx={{
                mb: 0.75,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                <Typography
                  variant="subtitle2"
                  sx={{
                    fontSize: 14,
                    fontWeight: 600,
                    color: "#343A40",
                  }}
                >
                  {label}
                  {required && (
                    <Box component="span" sx={{ color: "#F05252", ml: 0.25 }}>
                      *
                    </Box>
                  )}
                </Typography>

                {tooltip && (
                  <HelpTooltip
                    title={tooltip}
                    arrow
                    enterTouchDelay={0}
                    leaveTouchDelay={2000}
                  >
                    <IconButton
                      size="small"
                      sx={{
                        p: 0.25,
                        ml: 0.25,
                        color: "#6A7A89",
                      }}
                    >
                      <InfoOutlinedIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  </HelpTooltip>
                )}
              </Box>
            </Box>

            {/* INPUT */}
            <TextField
              {...rest}
              {...field}
              fullWidth
              error={!!fieldState.error}
              helperText={
                errorMsg
                  ? translateError(errorMsg)
                  : (helperText as React.ReactNode)
              }
              variant="outlined"
              InputProps={{
                ...rest.InputProps,
              }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: "8px",
                  backgroundColor: "#FFFFFF",
                  height: 46,
                  "& fieldset": {
                    borderColor: "#E4EDF5",
                  },
                  "&:hover fieldset": {
                    borderColor: "#B3C6DA",
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: "#3975D0",
                    boxShadow: "0 0 0 1px rgba(57, 117, 208, 0.15)",
                  },
                },

                "& .MuiInputBase-input": {
                  color: "#1A1A1A",
                  fontSize: { xs: 16, md: 14 },
                },

                "& .MuiInputBase-input::placeholder": {
                  color: "#A0AEC0",
                  opacity: 1,
                },
              }}
            />
          </Box>
        );
      }}
    />
  );
}
